/**
 * Object Snapping System (OSnap)
 * 
 * Provides 2D/3D-style object snapping for precise drawing.
 * Supports: Endpoint, Midpoint, Center, Intersection, Perpendicular, etc.
 */

import * as THREE from 'three';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';
import { QuadTree, type AABB } from '../../core/spatial/quadtree';

// ============================================================================
// TYPES
// ============================================================================

export type SnapType =
    | 'endpoint'      // Line/arc endpoints
    | 'midpoint'      // Midpoint of lines/arcs
    | 'center'        // Center of circles/arcs
    | 'node'          // Point objects
    | 'quadrant'      // Circle quadrants (0°, 90°, 180°, 270°)
    | 'intersection'  // Intersection of two objects
    | 'extension'     // Extension of lines
    | 'perpendicular' // Perpendicular to object
    | 'tangent'       // Tangent to circles/arcs
    | 'nearest'       // Nearest point on object
    | 'parallel'      // Parallel to reference line
    // Faz 7.9 additions (OCS snap.rs parity):
    | 'geometricCenter'       // Area centroid of a closed polyline
    | 'insertion'             // INSERT block origin
    | 'apparentIntersection'; // Extended crossings resolved in screen space
    // No 'grid' mode: grid stepping is F9 (gridSnapEnabled), not an object
    // snap — a grid osnap candidate sat within aperture at virtually every
    // cursor position, so the marker and the magnet never rested.

export interface SnapPoint {
    type: SnapType;
    position: THREE.Vector3;
    entityId?: string;      // Source entity ID
    distance: number;       // Distance from cursor
    priority: number;       // Lower = higher priority
}

export interface SnapSettings {
    enabled: boolean;
    aperture: number;       // Snap aperture in pixels
    gridSize: number;       // Grid snap size in units
    /** F9 grid stepping (classic 2D default: off). */
    gridSnapEnabled: boolean;
    types: Record<SnapType, boolean>;
    /** OTRACK master switch (F11, Faz 4.3): align to X/Y/45° lines through
     *  recently acquired osnap points. */
    objectSnapTracking: boolean;
    /** SELECTIONCYCLING display parity (Faz 4.4): stacked clicks open the
     *  candidate popup instead of silently advancing. */
    selectionCycling: boolean;
    /** Marker color style (Faz 11.2, OCS snap color style): 'perType' paints
     *  every glyph with its own snap color, 'classicYellow' the classic
     *  all-yellow single-ink scheme. */
    markerStyle: 'perType' | 'classicYellow';
}

/** One dashed OTRACK alignment guide drawn through a tracked reference.
 *  `type` carries the acquired osnap kind so the guide (and its acquisition
 *  mark) render in that snap's color (Faz 11.4). */
export interface TrackingGuide {
    from: [number, number, number];
    to: [number, number, number];
    type?: SnapType;
}

export interface SnapState extends SnapSettings {
    activeSnap: SnapPoint | null;
    candidates: SnapPoint[];
    /**
     * The world point the NEXT click will commit (osnap target when one is
     * acquired, otherwise the grid/polar/ortho-constrained cursor). Feeds
     * the statusbar coordinate readout so it always matches the marker and
     * the committed geometry. Transient — never persisted.
     */
    draftCursor: THREE.Vector3 | null;
    /** Live OTRACK alignment guides (transient; PolarTrackOverlay renders). */
    trackingGuides: TrackingGuide[];
    /** Marker color style switch (Faz 11.2): 'perType' (default) or
     *  'classicYellow' — DSETTINGS Object Snap tab writes it. */
    markerStyle: 'perType' | 'classicYellow';
    /**
     * One-shot osnap override (Faz 11.2, Shift+RMB grid): when set, the NEXT
     * snap query resolves ONLY this type — running snaps and the master
     * switch stand down for that single pick (classic osnap override). The
     * consumer clears it after the click commits. Transient — never persisted.
     */
    overrideType: SnapType | null;
    setEnabled: (enabled: boolean) => void;
    setAperture: (aperture: number) => void;
    setGridSize: (size: number) => void;
    setGridSnapEnabled: (enabled: boolean) => void;
    toggleGridSnap: () => void;
    toggleSnapType: (type: SnapType) => void;
    setSnapType: (type: SnapType, enabled: boolean) => void;
    enableOnly: (types: SnapType[]) => void;
    enableAll: () => void;
    disableAll: () => void;
    setActiveSnap: (snap: SnapPoint | null) => void;
    setCandidates: (candidates: SnapPoint[]) => void;
    setDraftCursor: (point: THREE.Vector3) => void;
    toggleObjectSnapTracking: () => void;
    toggleSelectionCycling: () => void;
    setTrackingGuides: (guides: TrackingGuide[]) => void;
    setMarkerStyle: (style: 'perType' | 'classicYellow') => void;
    setOverrideType: (type: SnapType | null) => void;
    consumeOverride: () => void;
}

// ============================================================================
// SNAP STORE
// ============================================================================

const defaultSnapTypes: Record<SnapType, boolean> = {
    endpoint: true,
    midpoint: true,
    center: true,
    node: true,
    quadrant: false,
    intersection: true,
    extension: false,
    perpendicular: true,
    tangent: false,
    nearest: false,
    parallel: false,
    // Faz 7.9: the screen-space apparent sweep is opt-in (cost guard);
    // insertion and geometric center are cheap pre-baked hints.
    insertion: true,
    geometricCenter: false,
    apparentIntersection: false,
};

export const useSnapStore = create<SnapState>()(
    persist(
        (set, get) => ({
            enabled: true,
            aperture: 10,           // 10 pixel snap aperture (classic APERTURE default)
            gridSize: 0.5,          // 0.5 unit grid
            gridSnapEnabled: false, // F9 grid stepping (classic default: off)
            types: { ...defaultSnapTypes },
            objectSnapTracking: false, // F11 OTRACK (classic default: off)
            selectionCycling: false,   // SELECTIONCYCLING popup (Faz 4.4)
            activeSnap: null,
            candidates: [],
            draftCursor: null,
            trackingGuides: [],
            markerStyle: 'perType',
            overrideType: null,

            setEnabled: (enabled) => set({ enabled }),
            setMarkerStyle: (markerStyle) => set({ markerStyle }),
            setOverrideType: (overrideType) => set({ overrideType }),
            /** One-shot override consumed by the click that commits it. */
            consumeOverride: () => {
                if (get().overrideType === null) return;
                set({ overrideType: null });
            },
            setAperture: (aperture) => set({ aperture }),
            setGridSize: (gridSize) => set({ gridSize }),
            setGridSnapEnabled: (gridSnapEnabled) => set({ gridSnapEnabled }),
            toggleGridSnap: () => set((state) => ({ gridSnapEnabled: !state.gridSnapEnabled })),

            toggleSnapType: (type) => set((state) => ({
                types: { ...state.types, [type]: !state.types[type] }
            })),

            setSnapType: (type, enabled) => set((state) => ({
                types: { ...state.types, [type]: enabled }
            })),

            enableOnly: (types) => set((state) => {
                const newTypes = { ...state.types };
                Object.keys(newTypes).forEach((t) => {
                    newTypes[t as SnapType] = types.includes(t as SnapType);
                });
                return { types: newTypes };
            }),

            enableAll: () => set({
                types: Object.fromEntries(
                    Object.keys(defaultSnapTypes).map(t => [t, true])
                ) as Record<SnapType, boolean>
            }),

            disableAll: () => set({
                types: Object.fromEntries(
                    Object.keys(defaultSnapTypes).map(t => [t, false])
                ) as Record<SnapType, boolean>
            }),

            // Pointer-move storms recompute the best snap on every mousemove,
            // usually landing on the same snap target. Content-equal writes
            // are dropped so the SnapIndicator only re-renders when the
            // feedback actually changes.
            setActiveSnap: (activeSnap) => set((state) => {
                const current = state.activeSnap;
                if (current === activeSnap) return state;
                if (current && activeSnap
                    && current.type === activeSnap.type
                    && current.entityId === activeSnap.entityId
                    && current.position.distanceToSquared(activeSnap.position) < 1e-8) {
                    return state;
                }
                return { activeSnap };
            }),
            setCandidates: (candidates) => set({ candidates }),
            // Pointer storms resolve the same cursor point many times over;
            // clone + content-equal drop keep the StatusBar from re-rendering
            // when the readout has not actually moved.
            setDraftCursor: (point) => set((state) => {
                if (state.draftCursor
                    && state.draftCursor.distanceToSquared(point) < 1e-8) {
                    return state;
                }
                return { draftCursor: point.clone() };
            }),

            toggleObjectSnapTracking: () => set((state) => ({ objectSnapTracking: !state.objectSnapTracking })),
            toggleSelectionCycling: () => set((state) => ({ selectionCycling: !state.selectionCycling })),
            // Content-equal drop: the constraint publishes per pointermove.
            setTrackingGuides: (guides) => set((state) => {
                if (state.trackingGuides.length === 0 && guides.length === 0) return state;
                return { trackingGuides: guides };
            }),
        }),
        {
            name: 'yap-snap-settings',
            storage: settingsStorage,
            // Persist the snap preferences; activeSnap/candidates are
            // transient hover feedback and must not be restored.
            partialize: (state) => ({
                enabled: state.enabled,
                aperture: state.aperture,
                gridSize: state.gridSize,
                gridSnapEnabled: state.gridSnapEnabled,
                types: state.types,
                objectSnapTracking: state.objectSnapTracking,
                selectionCycling: state.selectionCycling,
                markerStyle: state.markerStyle,
            }),
        }
    )
);

// ============================================================================
// SNAP PRIORITY (lower = higher priority)
// OCS snap.rs priority order (Faz 7.9 alignment): endpoint > midpoint >
// center > node > quadrant > intersection/apparent > extension > insertion >
// perpendicular > tangent > geometricCenter > nearest > parallel.
// ============================================================================

export const SNAP_PRIORITY: Record<SnapType, number> = {
    endpoint: 1,
    midpoint: 2,
    center: 3,
    node: 4,
    quadrant: 5,
    intersection: 6,
    apparentIntersection: 7,
    extension: 8,
    insertion: 9,
    perpendicular: 10,
    tangent: 11,
    geometricCenter: 12,
    nearest: 13,
    parallel: 14,
};

// ============================================================================
// SNAP ICONS (for UI display)
// ============================================================================

export const SNAP_ICONS: Record<SnapType, string> = {
    endpoint: '⬜',
    midpoint: '△',
    center: '○',
    node: 'ⓧ',
    quadrant: '◇',
    intersection: '✕',
    extension: '—',
    perpendicular: '⊥',
    tangent: '⌢',
    nearest: '∿',
    parallel: '∥',
    geometricCenter: '⊕',
    insertion: '⌖',
    apparentIntersection: '⨯',
};

// ============================================================================
// SNAP MANAGER CLASS
// ============================================================================

/** Apparent-intersection budget (Faz 7.9, OCS MAX_PAIRWISE_POINTS): the
 *  screen-space sweep runs only under this wire-point total; beyond it the
 *  nested wire-pair fallback takes over. */
const MAX_PAIRWISE_POINTS = 3000;

export class SnapManager {
    private entities: Map<string, EntitySnapData> = new Map();
    /**
     * Quadtree broad phase over the registered geometry (Faz 10.2, OCS
     * quadtree.rs parity): pointermove queries walk the aperture-box
     * candidate set instead of every registered entity. Bounds come from
     * the snap skeleton itself (never tessellation). Entries whose
     * geometry cannot be bounded (degenerate/non-finite) live in
     * `unindexed` and ride along on every query — an over-approximate
     * candidate set is always safe, a missing candidate is not.
     */
    private spatial = new QuadTree();
    private unindexed = new Set<string>();
    /** Overflow guard: out-of-root inserts degrade to linear scans, so the
     *  tree is rebuilt around the full bounds union once this many items
     *  piled into the overflow bag. */
    private static readonly OVERFLOW_REBUILD_LIMIT = 512;
    /**
     * Pre-computed snap points per entity (task: pointermove cost).
     * getEntitySnapPoints allocates Vector3s per shape per call; computing it
     * on every mousemove made the snap pass O(entity count) allocations per
     * event. Registration/unregistration invalidates the entry, so the cache
     * always mirrors the registered geometry and the hover loop becomes a
     * pure distance scan over reusable points.
     */
    private snapPointCache = new Map<string, SnapPoint[]>();
    private settings: SnapSettings;

    constructor(settings: SnapSettings) {
        this.settings = settings;
    }

    /**
     * Update settings
     */
    updateSettings(settings: SnapSettings): void {
        this.settings = settings;
    }

    /** Ids currently registered for entity snapping (for ledger diffing). */
    registeredIds(): string[] {
        return Array.from(this.entities.keys());
    }

    /**
     * Register an entity for snapping. Idempotent: re-registration drops
     * the stale tree/cache entries first, so the caller can mirror store
     * diffs without a clearEntities round-trip.
     */
    registerEntity(id: string, data: EntitySnapData): void {
        this.entities.set(id, data);
        // Pre-compute snap points once: pointermove only scans distances.
        this.snapPointCache.set(id, this.computeEntitySnapPoints(data, id));
        const bounds = snapDataBounds(data);
        this.unindexed.delete(id);
        this.spatial.remove(id);
        if (bounds) {
            this.spatial.insert({ handle: id, bounds });
            if (this.spatial.overflowSize > SnapManager.OVERFLOW_REBUILD_LIMIT) {
                this.rebuildSpatial();
            }
        } else {
            this.unindexed.add(id);
        }
    }

    /**
     * Unregister an entity
     */
    unregisterEntity(id: string): void {
        this.entities.delete(id);
        this.snapPointCache.delete(id);
        this.spatial.remove(id);
        this.unindexed.delete(id);
    }

    /**
     * Clear all registered entities
     */
    clearEntities(): void {
        this.entities.clear();
        this.snapPointCache.clear();
        this.spatial = new QuadTree();
        this.unindexed.clear();
    }

    /**
     * Broad-phase candidate ids for a world-XY rectangle: quadtree hits
     * plus the unbindable remainder. Non-finite query boxes (degenerate
     * camera math) fall back to "everything" — correctness over speed.
     */
    private candidatesInRect(rect: AABB): Set<string> {
        if (!rect.every(Number.isFinite)) {
            return new Set([...this.entities.keys()]);
        }
        const ids = new Set(this.spatial.queryRect(rect));
        for (const id of this.unindexed) ids.add(id);
        return ids;
    }

    /** Full tree rebuild around the union of all registered bounds. */
    private rebuildSpatial(): void {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const items: { handle: string; bounds: AABB }[] = [];
        for (const [id, data] of this.entities) {
            const bounds = snapDataBounds(data);
            if (!bounds) continue;
            items.push({ handle: id, bounds });
            if (bounds[0] < minX) minX = bounds[0];
            if (bounds[1] < minY) minY = bounds[1];
            if (bounds[2] > maxX) maxX = bounds[2];
            if (bounds[3] > maxY) maxY = bounds[3];
        }
        this.spatial = new QuadTree();
        this.unindexed.clear();
        for (const [id, data] of this.entities) {
            if (!snapDataBounds(data)) this.unindexed.add(id);
        }
        if (items.length === 0) return;
        const span = Math.max(maxX - minX, maxY - minY);
        const pad = span * 1e-9 + 1e-6;
        this.spatial.resetRoot([minX - pad, minY - pad, maxX + pad, maxY + pad]);
        for (const item of items) this.spatial.insert(item);
    }

    /**
     * Find snap points near cursor position.
     * `base` is the drawing base point (last committed point) when a
     * drafting command is mid-point: direction-dependent modes
     * (perpendicular) only produce candidates relative to it, matching the
     * classic OSNAP contract.
     */
    findSnapPoints(
        cursorWorld: THREE.Vector3,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        base?: THREE.Vector3 | null
    ): SnapPoint[] {
        // One-shot override (Faz 11.2, Shift+RMB grid): while set, this query
        // resolves ONLY the override type and stands down the master switch —
        // the classic single-pick osnap override semantics.
        const override = useSnapStore.getState().overrideType;
        const enabled = this.settings.enabled || !!override;
        const types: Record<SnapType, boolean> = override
            ? (Object.fromEntries(
                Object.keys(this.settings.types).map((t) => [t, t === override])
            ) as Record<SnapType, boolean>)
            : this.settings.types;
        if (!enabled) return [];

        const snapPoints: SnapPoint[] = [];
        const apertureWorld = pixelsToWorld(
            this.settings.aperture,
            cursorWorld,
            camera,
            renderer
        );
        const apertureSq = apertureWorld * apertureWorld;

        // Broad phase (Faz 10.2): the quadtree returns only entities whose
        // snap skeleton intersects the aperture box, so the distance scan
        // below touches O(log n + k) entries instead of every registration.
        const rect: AABB = [
            cursorWorld.x - apertureWorld,
            cursorWorld.y - apertureWorld,
            cursorWorld.x + apertureWorld,
            cursorWorld.y + apertureWorld,
        ];
        const candidates = this.candidatesInRect(rect);

        // Scan the pre-computed snap points of the candidate set: no
        // geometry re-tessellation and no Vector3 allocation per pointer
        // event, only squared distance tests.
        for (const id of candidates) {
            const entitySnaps = this.snapPointCache.get(id);
            if (!entitySnaps) continue;
            for (const snap of entitySnaps) {
                if (!types[snap.type]) continue;
                const distanceSq = snap.position.distanceToSquared(cursorWorld);
                if (distanceSq <= apertureSq) {
                    snapPoints.push({
                        type: snap.type,
                        // Clone: cached positions are shared references; the
                        // best snap flows into tool state and may be mutated.
                        position: snap.position.clone(),
                        entityId: snap.entityId,
                        distance: Math.sqrt(distanceSq),
                        priority: snap.priority,
                    });
                }
            }
        }

        // Dynamic cursor-relative snaps: these depend on the live cursor and
        // on pairs of objects, so they cannot be pre-cached per entity.
        // Intersection, extension, perpendicular, tangent and nearest are
        // computed over the registered geometry against the current cursor.
        if (types.intersection || types.extension
            || types.perpendicular || types.tangent
            || types.nearest || types.apparentIntersection) {
            this.collectDynamicSnaps(cursorWorld, apertureWorld, snapPoints, candidates, base, camera, renderer, types);
        }

        // Sort by priority, then by distance
        return snapPoints.sort((a, b) => {
            if (a.priority !== b.priority) return a.priority - b.priority;
            return a.distance - b.distance;
        });
    }

    /**
     * Cursor-relative OSNAP pass. Tests line/curve pairs against each other
     * (intersection) and against the cursor (tangent point, nearest rim, line
     * extension ray). Appends candidates within the aperture to `out`.
     *
     * Perpendicular is base-aware: the candidate is the foot of the
     * perpendicular FROM the drawing base onto the line, valid while the
     * cursor hovers that foot. Without a base (idle hover, first click) the
     * mode is silent — hovering a line then never drags the point onto it.
     *
     * Broad phase (performance): every dynamic candidate lies ON a segment or
     * on a circle rim, so its distance to the cursor being ≤ aperture implies
     * the carrier object passes within `aperture` of the cursor. Each object
     * is therefore first tested with pure scalar math (no Vector3/Vector2
     * allocation, no pair loops); the O(L²) intersection passes run only over
     * the surviving near-subset — typically 0–5 objects, instead of every
     * line in the document times itself on every pointermove.
     */
    private collectDynamicSnaps(
        cursor: THREE.Vector3,
        aperture: number,
        out: SnapPoint[],
        candidates: Set<string>,
        base?: THREE.Vector3 | null,
        camera?: THREE.Camera,
        renderer?: THREE.WebGLRenderer,
        typesOverride?: Record<SnapType, boolean>
    ): void {
        const types = typesOverride ?? this.settings.types;
        const ap2 = aperture * aperture;
        // Ownership moves: helpers return fresh Vector3s, out keeps them.
        const push = (type: SnapType, position: THREE.Vector3, entityId: string) => {
            out.push({ type, position, entityId, distance: position.distanceTo(cursor), priority: SNAP_PRIORITY[type] });
        };

        // ---- Broad-phase filter: scalar-only passes over the ledger ----
        // The near passes only see the quadtree candidate set (Faz 10.2);
        // extension keeps an unbounded scan because its ray can reach the
        // cursor from arbitrarily far outside the aperture box.
        interface NearLine { id: string; a: THREE.Vector3; b: THREE.Vector3; foot: THREE.Vector3; footDistSq: number }
        interface NearCurve { id: string; center: THREE.Vector3; radius: number }
        const nearLines: NearLine[] = [];
        const nearCurves: NearCurve[] = [];
        // Extension needs the infinite ray past each endpoint: a cursor far
        // along the ray can be far from the segment yet exactly on it, so
        // extension candidates are collected with their own scalar test.
        const extensionLines: { id: string; a: THREE.Vector3; b: THREE.Vector3 }[] = [];

        const needNear = types.nearest || types.intersection;
        const needTangent = types.tangent;
        const needCurve = needNear || needTangent || types.intersection;
        const needLinePair = types.intersection;
        const collectExtension = types.extension;

        this.entities.forEach((data, id) => {
            if (data.type === 'line') {
                if (collectExtension) extensionLines.push({ id, a: data.start, b: data.end });
                if (!candidates.has(id)) return;
                if (needLinePair || needNear) {
                    // Squared distance from cursor to segment, XY-plane with
                    // Z interpolated exactly like closestOnSegment3.
                    const a = data.start, b = data.end;
                    const abx = b.x - a.x, aby = b.y - a.y;
                    const len2 = abx * abx + aby * aby;
                    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((cursor.x - a.x) * abx + (cursor.y - a.y) * aby) / len2));
                    const fx = a.x + abx * t, fy = a.y + aby * t, fz = a.z + (b.z - a.z) * t;
                    const distSq = (fx - cursor.x) ** 2 + (fy - cursor.y) ** 2 + (fz - cursor.z) ** 2;
                    if (distSq <= ap2) {
                        nearLines.push({ id, a, b, foot: new THREE.Vector3(fx, fy, fz), footDistSq: distSq });
                    }
                }
            } else if (data.type === 'circle' || data.type === 'arc') {
                if (!needCurve || !candidates.has(id)) return;
                // Candidate points lie on the rim: |d(center) - r| ≤ aperture.
                const dx = cursor.x - data.center.x, dy = cursor.y - data.center.y;
                const d = Math.hypot(dx, dy);
                if (Math.abs(d - data.radius) <= aperture) {
                    nearCurves.push({ id, center: data.center, radius: data.radius });
                }
            }
        });

        // ---- Perpendicular: foot of the perpendicular from the base onto
        // each line (own pass — the foot may sit on the segment's extension,
        // outside the cursor broad phase). Silent without a base. The foot
        // is accepted only within the aperture of the cursor, and the foot
        // lies on the segment, so quadtree candidates fully cover the pass.
        if (types.perpendicular && base) {
            this.entities.forEach((data, id) => {
                if (data.type !== 'line' || !candidates.has(id)) return;
                const a = data.start, b = data.end;
                const abx = b.x - a.x, aby = b.y - a.y;
                const len2 = abx * abx + aby * aby;
                if (len2 < 1e-12) return;
                const t = ((base.x - a.x) * abx + (base.y - a.y) * aby) / len2;
                const fx = a.x + abx * t, fy = a.y + aby * t, fz = a.z + (b.z - a.z) * t;
                const dSq = (fx - cursor.x) ** 2 + (fy - cursor.y) ** 2 + (fz - cursor.z) ** 2;
                if (dSq <= ap2) {
                    push('perpendicular', new THREE.Vector3(fx, fy, fz), id);
                }
            });
        }

        // ---- Per-object candidates (allocation only for near objects) ----
        for (const l of nearLines) {
            if (types.nearest) out.push({ type: 'nearest', position: l.foot.clone(), entityId: l.id, distance: Math.sqrt(l.footDistSq), priority: SNAP_PRIORITY.nearest });
        }
        for (const c of nearCurves) {
            if (types.nearest) {
                const p = nearestOnCircle(cursor, c.center, c.radius);
                if (p.distanceToSquared(cursor) <= ap2) push('nearest', p, c.id);
            }
            if (types.tangent) {
                const t = tangentFromPoint(cursor, c.center, c.radius);
                if (t && t.distanceToSquared(cursor) <= ap2) push('tangent', t, c.id);
            }
        }

        // ---- Pair candidates: near-subset only ----
        if (types.intersection) {
            for (let i = 0; i < nearLines.length; i++) {
                for (let j = i + 1; j < nearLines.length; j++) {
                    const p = segmentIntersect2(nearLines[i].a, nearLines[i].b, nearLines[j].a, nearLines[j].b);
                    if (p && p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearLines[i].id);
                }
                for (const c of nearCurves) {
                    for (const p of lineCircleIntersect(nearLines[i].a, nearLines[i].b, c.center, c.radius)) {
                        if (p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearLines[i].id);
                    }
                }
            }
            for (let i = 0; i < nearCurves.length; i++) {
                for (let j = i + 1; j < nearCurves.length; j++) {
                    for (const p of circleCircleIntersect(nearCurves[i].center, nearCurves[i].radius, nearCurves[j].center, nearCurves[j].radius)) {
                        if (p.distanceToSquared(cursor) <= ap2) push('intersection', p, nearCurves[i].id);
                    }
                }
            }
        }

        if (types.extension) {
            // Both endpoints carry an extension ray (a→b past b, b→a past a),
            // matching the original two-orientation pass.
            for (const l of extensionLines) {
                const pastB = extensionSnap(l.a, l.b, cursor, aperture);
                if (pastB) push('extension', pastB, l.id);
                const pastA = extensionSnap(l.b, l.a, cursor, aperture);
                if (pastA) push('extension', pastA, l.id);
            }
        }

        // ---- Apparent intersection (Faz 7.9): extended crossings of wires
        // that do not touch, resolved in screen space (OCS snap.rs
        // 'apparent_sweep' verbatim: max 3000 wire points, min-X ordered
        // sweep with Y-overlap prefilter, budget overflow falls back to the
        // nested wire-pair loop).
        if (types.apparentIntersection && camera && renderer) {
            this.collectApparentIntersections(cursor, aperture, camera, renderer, out);
        }
    }

    /**
     * Apparent-intersection pass. Collects one wire skeleton per registered
     * entity (line/polyline/rectangle segments, sampled circle/arc rims),
     * projects the endpoints to screen pixels, then sweeps for crossings.
     * An intersection within the aperture of the cursor ends the sweep
     * (OCS breaks on the epsilon-close hit). Each screen-space hit maps
     * back through `a + ta*(b-a)` so the committed point is exact world
     * geometry, never a re-projection.
     */
    private collectApparentIntersections(
        cursor: THREE.Vector3,
        aperture: number,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        out: SnapPoint[]
    ): void {
        const size = renderer.getSize(_pxToWorldSize);
        const project = (p: THREE.Vector3): { x: number; y: number } => {
            const ndc = p.clone().project(camera);
            return { x: (ndc.x * 0.5 + 0.5) * size.x, y: (1 - (ndc.y * 0.5 + 0.5)) * size.y };
        };
        const cursorScreen = project(cursor);

        // One wire segment list per entity id (world a/b + screen a/b).
        interface ApparentSeg { id: string; ax: number; ay: number; bx: number; by: number; wa: THREE.Vector3; wb: THREE.Vector3 }
        const wires: ApparentSeg[] = [];
        const pushSeg = (id: string, wa: THREE.Vector3, wb: THREE.Vector3) => {
            const sa = project(wa), sb = project(wb);
            wires.push({ id, ax: sa.x, ay: sa.y, bx: sb.x, by: sb.y, wa, wb });
        };
        const rimSegs = (id: string, center: THREE.Vector3, radius: number, from: number, to: number, steps: number) => {
            let sweep = to - from;
            while (sweep <= 0) sweep += Math.PI * 2;
            for (let i = 0; i < steps; i++) {
                const a0 = from + (sweep * i) / steps, a1 = from + (sweep * (i + 1)) / steps;
                pushSeg(id, new THREE.Vector3(center.x + Math.cos(a0) * radius, center.y + Math.sin(a0) * radius, center.z),
                    new THREE.Vector3(center.x + Math.cos(a1) * radius, center.y + Math.sin(a1) * radius, center.z));
            }
        };
        this.entities.forEach((data, id) => {
            switch (data.type) {
                case 'line': {
                    const l = data as LineSnapData;
                    pushSeg(id, l.start, l.end);
                    break;
                }
                case 'polyline': {
                    const pd = data as PolylineSnapData;
                    const n = pd.vertices.length;
                    const segs = pd.closed ? n : n - 1;
                    for (let i = 0; i < segs; i++) pushSeg(id, pd.vertices[i], pd.vertices[(i + 1) % n]);
                    break;
                }
                case 'rectangle': {
                    const r = data as RectangleSnapData;
                    const corners = [
                        new THREE.Vector3(r.corner1.x, r.corner1.y, r.corner1.z),
                        new THREE.Vector3(r.corner2.x, r.corner1.y, r.corner1.z),
                        new THREE.Vector3(r.corner2.x, r.corner2.y, r.corner1.z),
                        new THREE.Vector3(r.corner1.x, r.corner2.y, r.corner1.z),
                    ];
                    for (let i = 0; i < 4; i++) pushSeg(id, corners[i], corners[(i + 1) % 4]);
                    break;
                }
                case 'circle': {
                    const c = data as CircleSnapData;
                    rimSegs(id, c.center, c.radius, 0, Math.PI * 2, 32);
                    break;
                }
                case 'arc': {
                    const a = data as ArcSnapData;
                    rimSegs(id, a.center, a.radius, a.startAngle, a.endAngle, 16);
                    break;
                }
            }
        });

        const totalPoints = wires.length * 2;
        const ap2 = aperture * aperture;

        const intersectPair = (s1: ApparentSeg, s2: ApparentSeg): { x: number; y: number; t: number } | null => {
            const rx = s1.bx - s1.ax, ry = s1.by - s1.ay;
            const sx = s2.bx - s2.ax, sy = s2.by - s2.ay;
            const denom = rx * sy - ry * sx;
            if (Math.abs(denom) < 1e-9) return null;
            const t = ((s2.ax - s1.ax) * sy - (s2.ay - s1.ay) * sx) / denom;
            const u = ((s2.ax - s1.ax) * ry - (s2.ay - s1.ay) * rx) / denom;
            const tol = 1e-9;
            if (t < -tol || t > 1 + tol || u < -tol || u > 1 + tol) return null;
            return { x: s1.ax + t * rx, y: s1.ay + t * ry, t };
        };
        const emit = (seg: ApparentSeg, t: number, sx: number, sy: number): boolean => {
            void sx; void sy;
            // World point through the segment parameter (exact geometry).
            const world = seg.wa.clone().addScaledVector(seg.wb.clone().sub(seg.wa), t);
            if (world.distanceToSquared(cursor) > ap2) return false;
            out.push({
                type: 'apparentIntersection',
                position: world,
                entityId: seg.id,
                distance: world.distanceTo(cursor),
                priority: SNAP_PRIORITY.apparentIntersection,
            });
            return true;
        };

        if (totalPoints > MAX_PAIRWISE_POINTS) {
            // Budget overflow: nested wire-pair fallback (OCS behavior).
            for (let i = 0; i < wires.length; i++) {
                for (let j = i + 1; j < wires.length; j++) {
                    const hit = intersectPair(wires[i], wires[j]);
                    if (hit && emit(wires[i], hit.t, hit.x, hit.y)) return;
                }
            }
            return;
        }

        // Screen-space sweep: min-X ordered, skip non-Y-overlapping pairs.
        const sorted = wires.map((s, idx) => ({ s, minX: Math.min(s.ax, s.bx), maxX: Math.max(s.ax, s.bx), minY: Math.min(s.ay, s.by), maxY: Math.max(s.ay, s.by) }))
            .sort((a, b) => a.minX - b.minX);
        for (let i = 0; i < sorted.length; i++) {
            const a = sorted[i];
            for (let j = i + 1; j < sorted.length; j++) {
                const b = sorted[j];
                // Sorted by minX: once b.minX passes a.maxX the rest follow.
                if (b.minX > a.maxX) break;
                if (b.maxY < a.minY || b.minY > a.maxY) continue;
                const hit = intersectPair(a.s, b.s);
                if (hit && emit(a.s, hit.t, hit.x, hit.y)) return;
            }
        }
    }

    /**
     * Get the best snap point (highest priority, closest)
     */
    getBestSnap(
        cursorWorld: THREE.Vector3,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        base?: THREE.Vector3 | null
    ): SnapPoint | null {
        const snaps = this.findSnapPoints(cursorWorld, camera, renderer, base);
        return snaps.length > 0 ? snaps[0] : null;
    }

    /**
     * Get snap points for a specific entity
     */
    private computeEntitySnapPoints(data: EntitySnapData, entityId: string): SnapPoint[] {
        return this.getEntitySnapPoints(data, entityId);
    }

    /**
     * Get snap points for a specific entity
     */
    private getEntitySnapPoints(data: EntitySnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        switch (data.type) {
            case 'line':
                points.push(...this.getLineSnaps(data as LineSnapData, entityId));
                break;
            case 'circle':
                points.push(...this.getCircleSnaps(data as CircleSnapData, entityId));
                break;
            case 'arc':
                points.push(...this.getArcSnaps(data as ArcSnapData, entityId));
                break;
            case 'rectangle':
                points.push(...this.getRectangleSnaps(data as RectangleSnapData, entityId));
                break;
            case 'box':
                points.push(...this.getBoxSnaps(data as BoxSnapData, entityId));
                break;
            case 'wall':
                points.push(...this.getWallSnaps(data as WallSnapData, entityId));
                break;
            case 'polyline': {
                // Vertex endpoints + per-segment midpoints: a polyline is
                // snapped as a chain of line snaps. `spline` (Faz 7.2) and
                // legacy `curve` entities register through this same shape
                // (their skeletons are mapped in snapSystemSync).
                const pd = data as PolylineSnapData;
                const vs = pd.vertices;
                const segCount = pd.closed ? vs.length : Math.max(vs.length - 1, 0);
                for (let i = 0; i < vs.length; i++) {
                    points.push({ type: 'endpoint', position: vs[i].clone(), entityId, distance: 0, priority: SNAP_PRIORITY.endpoint });
                }
                for (let i = 0; i < segCount; i++) {
                    const a = vs[i], b = vs[(i + 1) % vs.length];
                    points.push({ type: 'midpoint', position: a.clone().add(b).multiplyScalar(0.5), entityId, distance: 0, priority: SNAP_PRIORITY.midpoint });
                }
                // Geometric center (Faz 7.9): area centroid of the closed
                // loop, bulge arcs corrected via dense expansion.
                if (pd.closed && vs.length >= 3) {
                    const gc = this.geometricCenterOf(pd);
                    if (gc) {
                        points.push({ type: 'geometricCenter', position: gc, entityId, distance: 0, priority: SNAP_PRIORITY.geometricCenter });
                    }
                }
                break;
            }
            case 'point':
                points.push({
                    type: 'node',
                    position: new THREE.Vector3().copy((data as PointSnapData).position),
                    entityId,
                    distance: 0,
                    priority: SNAP_PRIORITY.node,
                });
                break;
            case 'insert':
                // Insertion osnap (Faz 7.9): the block reference origin.
                points.push({
                    type: 'insertion',
                    position: new THREE.Vector3().copy((data as InsertSnapData).position),
                    entityId,
                    distance: 0,
                    priority: SNAP_PRIORITY.insertion,
                });
                break;
        }

        return points;
    }

    /**
     * Line snap points: endpoints, midpoint
     */
    private getLineSnaps(data: LineSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Endpoints
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.start),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.end),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint
        const mid = new THREE.Vector3().addVectors(data.start, data.end).multiplyScalar(0.5);
        points.push({
            type: 'midpoint',
            position: mid,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

    /**
     * Circle snap points: center, quadrants
     */
    private getCircleSnaps(data: CircleSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(data.center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        // Quadrants (0°, 90°, 180°, 270°)
        const quadrantAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        quadrantAngles.forEach((angle) => {
            const x = data.center.x + data.radius * Math.cos(angle);
            const y = data.center.y + data.radius * Math.sin(angle);
            points.push({
                type: 'quadrant',
                position: new THREE.Vector3(x, y, data.center.z),
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.quadrant,
            });
        });

        return points;
    }

    /**
     * Arc snap points: endpoints, center, midpoint
     */
    private getArcSnaps(data: ArcSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(data.center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        // Start and end points
        const startX = data.center.x + data.radius * Math.cos(data.startAngle);
        const startY = data.center.y + data.radius * Math.sin(data.startAngle);
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3(startX, startY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        const endX = data.center.x + data.radius * Math.cos(data.endAngle);
        const endY = data.center.y + data.radius * Math.sin(data.endAngle);
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3(endX, endY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint of arc
        const midAngle = (data.startAngle + data.endAngle) / 2;
        const midX = data.center.x + data.radius * Math.cos(midAngle);
        const midY = data.center.y + data.radius * Math.sin(midAngle);
        points.push({
            type: 'midpoint',
            position: new THREE.Vector3(midX, midY, data.center.z),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

    /**
     * Rectangle snap points: corners, midpoints, center
     */
    private getRectangleSnaps(data: RectangleSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];
        const { corner1, corner2 } = data;

        // Corners (endpoints)
        // Z-up: corners vary in X/Y; both lie at corner1's level (Z).
        const corners = [
            new THREE.Vector3(corner1.x, corner1.y, corner1.z),
            new THREE.Vector3(corner2.x, corner1.y, corner1.z),
            new THREE.Vector3(corner2.x, corner2.y, corner1.z),
            new THREE.Vector3(corner1.x, corner2.y, corner1.z),
        ];

        corners.forEach((corner) => {
            points.push({
                type: 'endpoint',
                position: corner,
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.endpoint,
            });
        });

        // Edge midpoints
        for (let i = 0; i < 4; i++) {
            const mid = new THREE.Vector3()
                .addVectors(corners[i], corners[(i + 1) % 4])
                .multiplyScalar(0.5);
            points.push({
                type: 'midpoint',
                position: mid,
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.midpoint,
            });
        }

        // Center
        const center = new THREE.Vector3()
            .addVectors(corner1, corner2)
            .multiplyScalar(0.5);
        points.push({
            type: 'center',
            position: center,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        // Geometric center (Faz 7.9): a rectangle is a closed 4-corner loop.
        points.push({
            type: 'geometricCenter',
            position: center.clone(),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.geometricCenter,
        });

        return points;
    }

    /**
     * Box snap points: 8 corners, edge midpoints, face centers, box center
     */
    private getBoxSnaps(data: BoxSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];
        const { center, size } = data;
        const half = new THREE.Vector3(size.x / 2, size.y / 2, size.z / 2);

        // 8 corners
        const signs = [[-1, -1, -1], [1, -1, -1], [1, -1, 1], [-1, -1, 1],
        [-1, 1, -1], [1, 1, -1], [1, 1, 1], [-1, 1, 1]];

        signs.forEach(([sx, sy, sz]) => {
            points.push({
                type: 'endpoint',
                position: new THREE.Vector3(
                    center.x + sx * half.x,
                    center.y + sy * half.y,
                    center.z + sz * half.z
                ),
                entityId,
                distance: 0,
                priority: SNAP_PRIORITY.endpoint,
            });
        });

        // Box center
        points.push({
            type: 'center',
            position: new THREE.Vector3().copy(center),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.center,
        });

        return points;
    }

    /**
     * Wall snap points: endpoints of base line, midpoint
     */
    private getWallSnaps(data: WallSnapData, entityId: string): SnapPoint[] {
        const points: SnapPoint[] = [];

        // Wall endpoints (base line)
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.start),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });
        points.push({
            type: 'endpoint',
            position: new THREE.Vector3().copy(data.end),
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.endpoint,
        });

        // Midpoint
        const mid = new THREE.Vector3().addVectors(data.start, data.end).multiplyScalar(0.5);
        points.push({
            type: 'midpoint',
            position: mid,
            entityId,
            distance: 0,
            priority: SNAP_PRIORITY.midpoint,
        });

        return points;
    }

    /**
     * Area centroid of a closed loop (shoelace), bulge arcs corrected by
     * expanding them into dense arc samples first (Faz 7.9 — OCS pre-bakes
     * the same corrected centroid into its SnapHint::GeometricCenter).
     */
    private geometricCenterOf(pd: PolylineSnapData): THREE.Vector3 | null {
        const vs = pd.vertices;
        let ring: { x: number; y: number; z: number }[] = vs.map((p) => ({ x: p.x, y: p.y, z: p.z }));
        if (pd.bulges && pd.bulges.some((b) => Math.abs(b) > 1e-12)) {
            // Dense expansion keeps the shoelace area honest on arc segments.
            const dense: { x: number; y: number; z: number }[] = [];
            const n = vs.length;
            const segCount = pd.closed ? n : n - 1;
            for (let i = 0; i < segCount; i++) {
                const a = vs[i], b = vs[(i + 1) % n];
                const bulge = pd.bulges[i] ?? 0;
                dense.push({ x: a.x, y: a.y, z: a.z });
                if (Math.abs(bulge) > 1e-12) {
                    // Sample the arc between a and b (theta = 4*atan(bulge)).
                    const theta = 4 * Math.atan(bulge);
                    const chx = b.x - a.x, chy = b.y - a.y;
                    const chord = Math.hypot(chx, chy);
                    if (chord < 1e-9) continue;
                    const r = chord / (2 * Math.sin(theta / 2));
                    // Midpoint normal offset: apex of the bulge arc.
                    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
                    const h = r * Math.cos(theta / 2) * Math.sign(bulge);
                    const nx = -chy / chord, ny = chx / chord;
                    const steps = 16;
                    const sax = Math.atan2(a.y - (my + ny * h), a.x - (mx + nx * h));
                    const eax = Math.atan2(b.y - (my + ny * h), b.x - (mx + nx * h));
                    let sweep = eax - sax;
                    while (sweep > Math.PI) sweep -= 2 * Math.PI;
                    while (sweep < -Math.PI) sweep += 2 * Math.PI;
                    for (let s = 1; s < steps; s++) {
                        const ang = sax + (sweep * s) / steps;
                        dense.push({
                            x: mx + nx * h + r * Math.cos(ang),
                            y: my + ny * h + r * Math.sin(ang),
                            z: a.z,
                        });
                    }
                }
            }
            if (dense.length >= 3) ring = dense;
        }
        let a2 = 0, cx = 0, cy = 0;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const cross = ring[j].x * ring[i].y - ring[i].x * ring[j].y;
            a2 += cross;
            cx += (ring[j].x + ring[i].x) * cross;
            cy += (ring[j].y + ring[i].y) * cross;
        }
        if (Math.abs(a2) < 1e-12) return null;
        return new THREE.Vector3(cx / (3 * a2), cy / (3 * a2), vs[0].z);
    }

}

// ============================================================================
// ENTITY SNAP DATA TYPES
// ============================================================================

export interface BaseSnapData {
    type: string;
}

export interface PointSnapData extends BaseSnapData {
    type: 'point';
    position: THREE.Vector3;
}

export interface LineSnapData extends BaseSnapData {
    type: 'line';
    start: THREE.Vector3;
    end: THREE.Vector3;
}

export interface CircleSnapData extends BaseSnapData {
    type: 'circle';
    center: THREE.Vector3;
    radius: number;
}

export interface ArcSnapData extends BaseSnapData {
    type: 'arc';
    center: THREE.Vector3;
    radius: number;
    startAngle: number;
    endAngle: number;
}

export interface RectangleSnapData extends BaseSnapData {
    type: 'rectangle';
    corner1: THREE.Vector3;
    corner2: THREE.Vector3;
}

export interface BoxSnapData extends BaseSnapData {
    type: 'box';
    center: THREE.Vector3;
    size: THREE.Vector3;
}

export interface WallSnapData extends BaseSnapData {
    type: 'wall';
    start: THREE.Vector3;
    end: THREE.Vector3;
    height: number;
}

export interface PolylineSnapData extends BaseSnapData {
    type: 'polyline';
    vertices: THREE.Vector3[];
    closed?: boolean;
    /** Per-vertex bulges (arc segments) — the geometric-center area
     *  correction needs them (Faz 7.9). */
    bulges?: number[];
}

/** INSERT block reference: only the origin snaps (insertion mode). */
export interface InsertSnapData extends BaseSnapData {
    type: 'insert';
    position: THREE.Vector3;
}

export type EntitySnapData =
    | PointSnapData
    | LineSnapData
    | CircleSnapData
    | ArcSnapData
    | RectangleSnapData
    | BoxSnapData
    | WallSnapData
    | PolylineSnapData
    | InsertSnapData;

// ============================================================================
// SINGLETON MANAGER INSTANCE
// ============================================================================

let snapManagerInstance: SnapManager | null = null;

export function getSnapManager(): SnapManager {
    if (!snapManagerInstance) {
        const state = useSnapStore.getState();
        snapManagerInstance = new SnapManager({
            enabled: state.enabled,
            aperture: state.aperture,
            gridSize: state.gridSize,
            gridSnapEnabled: state.gridSnapEnabled,
            types: state.types,
            objectSnapTracking: state.objectSnapTracking,
            selectionCycling: state.selectionCycling,
            markerStyle: state.markerStyle,
        });
    }
    return snapManagerInstance;
}

export function resetSnapManager(): void {
    snapManagerInstance = null;
}

// ============================================================================
// PIXEL → WORLD CONVERSION (shared with pixel-fixed overlays)
// ============================================================================

const _pxToWorldSize = new THREE.Vector2();

/**
 * Convert a pixel distance to world units at the given point's depth.
 * Zoom-aware: the app zooms the orthographic plan cameras through
 * `camera.zoom` while the frustum top/bottom stay fixed, so the frustum
 * height must be divided by zoom — an aperture measured without it inflates
 * by the zoom factor and snap targets grab the cursor from far away when
 * zoomed in (and go deaf when zoomed out).
 */
export function pixelsToWorld(
    pixels: number,
    point: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer
): number {
    const size = renderer.getSize(_pxToWorldSize);
    const zoom = (camera as THREE.OrthographicCamera).zoom || 1;

    if (camera instanceof THREE.PerspectiveCamera) {
        const distance = camera.position.distanceTo(point);
        const vFov = (camera.fov * Math.PI) / 180;
        const worldHeight = 2 * distance * Math.tan(vFov / 2);
        return (pixels / size.y) * (worldHeight / zoom);
    }
    // Orthographic camera
    const ortho = camera as THREE.OrthographicCamera;
    return (pixels / size.y) * ((ortho.top - ortho.bottom) / zoom);
}

// ============================================================================
// DYNAMIC-SNAP GEOMETRY HELPERS (cursor-relative candidate computation)
// ============================================================================

// The per-segment perpendicular foot (nearest/perpendicular candidates) is
// computed inline by collectDynamicSnaps' scalar broad phase; the old
// closestOnSegment3 helper became dead when the pair loops were gated.

/** Nearest point on a circle's rim to `p` (in XY, level preserved). */
function nearestOnCircle(p: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3 {
    const dx = p.x - center.x, dy = p.y - center.y;
    const d = Math.hypot(dx, dy) || 1;
    return new THREE.Vector3(center.x + (dx / d) * radius, center.y + (dy / d) * radius, center.z);
}

/** Tangent point on a circle (XY) from an external point `p`. Null inside. */
function tangentFromPoint(p: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3 | null {
    const dx = p.x - center.x, dy = p.y - center.y;
    const d2 = dx * dx + dy * dy, r2 = radius * radius;
    if (d2 < r2) return null;
    const a = r2 / d2, b = radius * Math.sqrt(Math.max(0, d2 - r2)) / d2;
    const t1x = center.x + a * dx - b * dy, t1y = center.y + a * dy + b * dx;
    const t2x = center.x + a * dx + b * dy, t2y = center.y + a * dy - b * dx;
    const d1 = (t1x - p.x) ** 2 + (t1y - p.y) ** 2;
    const d2b = (t2x - p.x) ** 2 + (t2y - p.y) ** 2;
    return d1 <= d2b
        ? new THREE.Vector3(t1x, t1y, center.z)
        : new THREE.Vector3(t2x, t2y, center.z);
}

/** Finite-segment 2D intersection (XY), or null. */
function segmentIntersect2(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3): THREE.Vector3 | null {
    const r = { x: b.x - a.x, y: b.y - a.y };
    const s = { x: d.x - c.x, y: d.y - c.y };
    const denom = r.x * s.y - r.y * s.x;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / denom;
    const u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / denom;
    const tol = 1e-9;
    if (t < -tol || t > 1 + tol || u < -tol || u > 1 + tol) return null;
    return new THREE.Vector3(a.x + t * r.x, a.y + t * r.y, a.z + (b.z - a.z) * t);
}

/** Points where a finite segment (XY) crosses a circle. */
function lineCircleIntersect(a: THREE.Vector3, b: THREE.Vector3, center: THREE.Vector3, radius: number): THREE.Vector3[] {
    const dx = b.x - a.x, dy = b.y - a.y;
    const fx = a.x - center.x, fy = a.y - center.y;
    const A = dx * dx + dy * dy;
    if (A < 1e-12) return [];
    const B = 2 * (fx * dx + fy * dy);
    const C = fx * fx + fy * fy - radius * radius;
    const disc = B * B - 4 * A * C;
    if (disc < 0) return [];
    const sq = Math.sqrt(disc);
    const ts = [(-B - sq) / (2 * A), (-B + sq) / (2 * A)].filter((t) => t >= -1e-9 && t <= 1 + 1e-9);
    return ts.map((t) => new THREE.Vector3(a.x + dx * t, a.y + dy * t, center.z));
}

/** Two-circle intersection points (XY). */
function circleCircleIntersect(c1: THREE.Vector3, r1: number, c2: THREE.Vector3, r2: number): THREE.Vector3[] {
    const dx = c2.x - c1.x, dy = c2.y - c1.y;
    const d = Math.hypot(dx, dy);
    if (d < 1e-9 || d > r1 + r2 || d < Math.abs(r1 - r2)) return [];
    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
    const mx = c1.x + a * dx / d, my = c1.y + a * dy / d;
    const rx = -dy / d * h, ry = dx / d * h;
    const out = [new THREE.Vector3(mx + rx, my + ry, c1.z), new THREE.Vector3(mx - rx, my - ry, c1.z)];
    return h < 1e-6 ? [out[0]] : out;
}

/**
 * Extension snap: the cursor projects onto the ray continuing past segment
 * endpoint `b` (away from `a`); returns the foot when it is past b (not on
 * the segment itself) and within `aperture`, like the classic EXT object snap.
 */
function extensionSnap(a: THREE.Vector3, b: THREE.Vector3, cursor: THREE.Vector3, aperture: number): THREE.Vector3 | null {
    const dir = new THREE.Vector2(b.x - a.x, b.y - a.y);
    if (dir.lengthSq() < 1e-12) return null;
    dir.normalize();
    const rel = new THREE.Vector2(cursor.x - b.x, cursor.y - b.y);
    const along = rel.dot(dir);
    if (along < 0) return null; // cursor is not past the b endpoint
    const perp = new THREE.Vector2(-dir.y, dir.x);
    const dist = Math.abs(rel.dot(perp));
    if (dist > aperture) return null;
    return new THREE.Vector3(b.x + dir.x * along, b.y + dir.y * along, b.z);
}

/**
 * Conservative world-XY bounds of one snap skeleton. Snap points always
 * lie on or inside the skeleton's geometry (endpoints, midpoints, centers,
 * rim samples), so a conservative geometry box is also a snap-point box —
 * the broad phase can never drop a real candidate. Returns null when the
 * skeleton has no finite extent (goes to the `unindexed` ride-along bag).
 */
function snapDataBounds(data: EntitySnapData): AABB | null {
    switch (data.type) {
        case 'line':
        case 'wall': {
            const d = data as LineSnapData;
            return finiteBounds(d.start, d.end);
        }
        case 'polyline': {
            // Vertex box only: chord midpoints and the closed-loop centroid
            // (shoelace) never leave the vertex hull, so bulge arcs need no
            // expansion for snap purposes.
            const d = data as PolylineSnapData;
            if (d.vertices.length === 0) return null;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const v of d.vertices) {
                if (!Number.isFinite(v.x) || !Number.isFinite(v.y)) return null;
                if (v.x < minX) minX = v.x;
                if (v.y < minY) minY = v.y;
                if (v.x > maxX) maxX = v.x;
                if (v.y > maxY) maxY = v.y;
            }
            return [minX, minY, maxX, maxY];
        }
        case 'circle': {
            const d = data as CircleSnapData;
            return radialBounds(d.center, d.radius);
        }
        case 'arc': {
            // Full-rim conservative box (cheap; snap points sit on the rim
            // and at the center, all inside).
            const d = data as ArcSnapData;
            return radialBounds(d.center, d.radius);
        }
        case 'rectangle': {
            const d = data as RectangleSnapData;
            return finiteBounds(d.corner1, d.corner2);
        }
        case 'box': {
            const d = data as BoxSnapData;
            const halfX = Math.abs(d.size.x) / 2, halfY = Math.abs(d.size.y) / 2;
            if (!Number.isFinite(d.center.x) || !Number.isFinite(d.center.y)) return null;
            return [d.center.x - halfX, d.center.y - halfY, d.center.x + halfX, d.center.y + halfY];
        }
        case 'point':
        case 'insert': {
            const p = (data as PointSnapData).position;
            if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
            return [p.x, p.y, p.x, p.y];
        }
        default:
            return null;
    }
}

function finiteBounds(a: THREE.Vector3, b: THREE.Vector3): AABB | null {
    if (!Number.isFinite(a.x) || !Number.isFinite(a.y) || !Number.isFinite(b.x) || !Number.isFinite(b.y)) return null;
    return [Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y)];
}

function radialBounds(center: THREE.Vector3, radius: number): AABB | null {
    if (!Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(radius)) return null;
    return [center.x - radius, center.y - radius, center.x + radius, center.y + radius];
}
